import { Component } from '@theme/component';

const MINUTE = 60 * 1000;
const DAY = 24 * 60; // minutes, for the weekly visibility window

/**
 * "Delivery estimate" block: counts down to the order cut-off hour and shows the dates of the
 * three delivery steps. All maths happen in the store's time zone (`data-utc-offset`, e.g. "+0200").
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [countdown]
 * @property {HTMLElement} [eta]
 * @property {HTMLElement[]} [dates]
 *
 * @extends Component<Refs>
 */
class DeliveryEstimate extends Component {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#update();
    this.#timer = window.setInterval(() => this.#update(), MINUTE);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#timer);
  }

  get #offsetMinutes() {
    const match = /^([+-])(\d{2})(\d{2})$/.exec(this.dataset.utcOffset ?? '');
    if (!match) return -new Date().getTimezoneOffset();
    const sign = match[1] === '-' ? -1 : 1;
    return sign * (Number(match[2]) * 60 + Number(match[3]));
  }

  /** Current time expressed as a "wall clock" date in the store's time zone. */
  get #storeNow() {
    const now = new Date();
    return new Date(now.getTime() + (this.#offsetMinutes + now.getTimezoneOffset()) * MINUTE);
  }

  /**
   * @param {Date} date
   * @param {number} days
   */
  #addBusinessDays(date, days) {
    const result = new Date(date);
    const excludeWeekends = this.dataset.excludeWeekends !== 'false';
    let added = 0;

    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (!excludeWeekends || (day !== 0 && day !== 6)) added += 1;
    }

    return result;
  }

  /**
   * The weekly window the block is allowed to show in, as minutes since Sunday 00:00 in the
   * store's time zone. `null` when the merchant has not switched scheduling on.
   *
   * @returns {{ start: number, end: number } | null}
   */
  get #window() {
    if (this.dataset.schedule !== 'true') return null;

    const start = Number(this.dataset.scheduleStartDay ?? 0) * DAY + Number(this.dataset.scheduleStartHour ?? 0) * 60;
    const end = Number(this.dataset.scheduleEndDay ?? 6) * DAY + Number(this.dataset.scheduleEndHour ?? 23) * 60;

    return Number.isFinite(start) && Number.isFinite(end) ? { start, end } : null;
  }

  /**
   * Shows or hides the block according to its weekly window and returns whether it is open.
   * The window is read in store time, so a shopper in another country sees the same schedule
   * the warehouse works to. In the theme editor the block is always shown — a merchant has to
   * be able to configure a block that happens to be out of hours right now.
   *
   * @param {Date} now
   */
  #applyWindow(now) {
    const range = this.#window;

    if (!range || this.dataset.schedulePreview === 'true') {
      this.hidden = false;
      return true;
    }

    const minutes = now.getDay() * DAY + now.getHours() * 60 + now.getMinutes();
    const { start, end } = range;
    // start === end is read as "always", and a start later than the end simply wraps the weekend.
    const open = start === end || (start < end ? minutes >= start && minutes < end : minutes >= start || minutes < end);

    this.hidden = !open;
    return open;
  }

  /**
   * @param {Date} date
   */
  #formatDate(date) {
    try {
      return new Intl.DateTimeFormat(this.dataset.locale || undefined, { day: 'numeric', month: 'short' }).format(date);
    } catch (error) {
      return `${date.getDate()}/${date.getMonth() + 1}`;
    }
  }

  #update() {
    const now = this.#storeNow;

    // Outside the window there is nothing to promise, so skip the maths entirely.
    if (!this.#applyWindow(now)) return;

    const cutoffHour = Number(this.dataset.cutoffHour ?? 14);
    const dispatchDays = Number(this.dataset.dispatchDays ?? 0);
    const deliveryDays = Number(this.dataset.deliveryDays ?? 2);

    // Orders after the cut-off (or on a weekend, when excluded) are treated as placed on the next business day.
    const cutoff = new Date(now);
    cutoff.setHours(cutoffHour, 0, 0, 0);

    let base = new Date(now);
    base.setHours(0, 0, 0, 0);
    if (now >= cutoff) {
      base = this.#addBusinessDays(base, 1);
      cutoff.setTime(base.getTime());
      cutoff.setHours(cutoffHour, 0, 0, 0);
    } else if (this.dataset.excludeWeekends !== 'false' && (base.getDay() === 0 || base.getDay() === 6)) {
      base = this.#addBusinessDays(base, 1);
      cutoff.setTime(base.getTime());
      cutoff.setHours(cutoffHour, 0, 0, 0);
    }

    const remaining = Math.max(0, cutoff.getTime() - now.getTime());
    const hours = Math.floor(remaining / (60 * MINUTE));
    const minutes = Math.floor((remaining % (60 * MINUTE)) / MINUTE);
    const template = this.dataset.timeFormat || '[h] h [m] min';

    const dispatch = this.#addBusinessDays(base, dispatchDays);
    const delivery = this.#addBusinessDays(dispatch, deliveryDays);
    // "On its way" step: the day after dispatch, never later than the delivery day
    const shippingCandidate = this.#addBusinessDays(dispatch, 1);
    const shipping = shippingCandidate > delivery ? delivery : shippingCandidate;

    const { countdown, eta, dates = [] } = this.refs;
    if (countdown) {
      countdown.textContent = template.replace('[h]', String(hours)).replace('[m]', String(minutes).padStart(2, '0'));
    }
    if (eta) eta.textContent = this.#formatDate(delivery);

    // "Order placed" is today: the shopper is ordering now. `base` is the day the order enters the
    // warehouse queue, which after the cut-off is already tomorrow and read as a wrong order date.
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const stepDates = [today, shipping, delivery];
    dates.forEach((element, index) => {
      const date = stepDates[index];
      if (date) element.textContent = this.#formatDate(date);
    });

    this.setAttribute('data-ready', '');
  }
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
